import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, client, result, amount, type Schema } from './api';
import { SourceReferences } from './SourcePicker';

type Issue = Schema['ProtocolIssue'];
type Decision = Schema['DiscrepancyDecision'];
type Draft = {
  state: Decision['state'];
  cause: Decision['cause'];
  reason: string;
  expectedEventId: string | null;
  busy: boolean;
  error: string;
  conflict: boolean;
};
type DecisionController = {
  draft: Draft;
  change: (patch: Partial<Draft>) => void;
  save: () => Promise<void>;
  reset: () => void;
};

function initialDraft(issue: Issue): Draft {
  const states: string[] = ['open', 'designer', 'confirmed', 'excluded', 'resolved'];
  return {
    state: states.includes(issue.decision_state) ? issue.decision_state as Decision['state'] : 'open',
    cause: null,
    reason: '',
    expectedEventId: issue.decision_id ?? null,
    busy: false,
    error: '',
    conflict: false,
  };
}

/** Kept above both renderers: collapsing/filtering/switching cannot lose a draft.
 * The original event ID is captured when editing begins, not silently rebased. */
export function useProtocolDecisions(revisionId: string, saved: () => Promise<unknown>) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const pending = useRef(new Set<string>());
  const cache = useQueryClient();
  return (issue: Issue): DecisionController => {
    const key = JSON.stringify([revisionId, issue.id]);
    const draft = drafts[key] ?? initialDraft(issue);
    const change = (patch: Partial<Draft>) => setDrafts(previous => ({
      ...previous, [key]: {...(previous[key] ?? initialDraft(issue)), ...patch},
    }));
    const save = async () => {
      if (!issue.work_id || !draft.reason.trim() || draft.conflict || pending.current.has(key)) return;
      pending.current.add(key);
      change({busy: true, error: ''});
      try {
        const updated = await result(client.POST('/api/v1/works/{work_id}/discrepancies/decision', {
          params: {path: {work_id: issue.work_id}},
          body: {issue_id: issue.id, cause: draft.cause, state: draft.state,
            reason: draft.reason, expected_event_id: draft.expectedEventId},
        }));
        cache.setQueryData<Issue[]>(['protocol', revisionId], previous => previous?.map(row => row.id === updated.id ? updated : row));
        await saved();
        await cache.invalidateQueries({queryKey: ['history', issue.work_id]});
        setDrafts(previous => {
          const next = {...previous};
          delete next[key];
          return next;
        });
      } catch (error) {
        const conflict = error instanceof ApiError && error.status === 409;
        const changedIssue = conflict && error.message === 'Замечание изменилось. Откройте актуальный протокол';
        change({error: changedIssue
          ? `${error.message}. Скопируйте текст черновика перед обновлением раздела: он относится к прежнему замечанию.`
          : String(error), busy: false, conflict});
        // A replaced issue has a new ID: keep this form available for copying.
        if (conflict && !changedIssue) await saved();
      } finally {
        pending.current.delete(key);
      }
    };
    const reset = () => setDrafts(previous => {
      const next = {...previous};
      delete next[key];
      return next;
    });
    return {draft, change, save, reset};
  };
}

export function ProtocolIssueBody({issue: i, revisionId, canEdit, openSource, openComparison, decision}: {
  issue: Issue;
  revisionId: string;
  canEdit: boolean;
  openSource: (id: string, key?: string) => void;
  openComparison: (issue: Issue) => void;
  decision: DecisionController;
}) {
  return <>
    {i.left && i.right && <button className="quiet" onClick={() => openComparison(i)}>Сопоставить источники рядом</button>}
    {i.left && i.right ? <>
      <div className="comparison-sources">{[i.left, i.right].map(m => <div key={m.id}>
        <span>{m.label}</span><strong>{m.value == null ? 'Неизвестно' : amount(m.value)} {m.unit}</strong>
        <SourceReferences revisionId={revisionId} value={m.source_ids} addresses={m.addresses} openSource={openSource}/>
      </div>)}</div>
      {i.difference != null && <p>Разница: {amount(i.difference)} {i.unit} · {i.percent == null ? 'Процент не определён' : `${Number(i.percent).toLocaleString('ru-RU', {maximumFractionDigits: 2})}%`} · база: {i.base || 'не определена'}</p>}
    </> : <SourceReferences revisionId={revisionId} value={i.source_ids} addresses={i.addresses} openSource={openSource}/>}
    {i.formula && <details><summary>Как посчитано</summary><p>{i.formula}</p></details>}
    {i.position && <p className="muted">{i.position}{i.building && ` · ${i.building}`}</p>}
    {i.decision_reason && <p className="muted">Решение: {i.decision_reason}</p>}
    {canEdit && i.work_id && <IssueDecision issue={i} decision={decision}/>}
  </>;
}

function IssueDecision({issue, decision: {draft, change, save, reset}}: {issue: Issue; decision: DecisionController}) {
  return <details><summary>Записать решение</summary>
    <form onSubmit={event => {event.preventDefault(); void save();}}>
      <fieldset disabled={draft.busy} style={{border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 12}}>
        <label>Состояние<select value={draft.state} onChange={event => change({state: event.target.value as Decision['state']})}>
          <option value="open">Открыто</option><option value="designer">Требует уточнения проектировщиком</option>
          <option value="confirmed">Подтвердить расхождение</option><option value="excluded">Исключить замечание</option>
          <option value="resolved">Рассмотрено — записать решение</option>
        </select></label>
        <label>Причина<select value={draft.cause || ''} onChange={event => change({cause: (event.target.value || null) as Decision['cause']})}>
          <option value="">Не указана</option><option value="recognition">Ошибка распознавания</option>
          <option value="duplicate">Дублирование</option><option value="real">Реальное отличие документов</option>
          <option value="other">Другая причина</option>
        </select></label>
        <label>Что решили и на каком основании<textarea required value={draft.reason} onChange={event => change({reason: event.target.value})}/></label>
        <p className="muted">Решение сохранится в истории. Объём меняется отдельно через корректировку работы.</p>
        {draft.error && <p role="alert">{draft.error}</p>}
        {draft.conflict && <div role="status">
          <p>Черновик остаётся в этой форме. Перед повторной записью сравните его с актуальным решением.</p>
          {(issue.decision_id ?? null) !== draft.expectedEventId ? <>
            <p><strong>{issue.status}</strong>{issue.decision_reason && ` · ${issue.decision_reason}`}</p>
            <button type="button" onClick={() => change({expectedEventId: issue.decision_id ?? null, conflict: false, error: ''})}>Использовать актуальную версию решения</button>
          </> : <p>Актуальное основание пока не получено. Обновите раздел перед новой записью.</p>}
        </div>}
        <button disabled={draft.busy || draft.conflict || !draft.reason.trim()}>Сохранить решение</button>
        {(draft.reason || draft.error) && <button type="button" className="quiet" onClick={reset}>Отменить черновик</button>}
      </fieldset>
    </form>
  </details>;
}
